import type { EnemyType } from './types';
import type { WaveDef } from './waves';

export interface ScoreInputs {
  enemiesDefeated: number;
  livesRemaining: number;
  wavesCleared: number;
  won: boolean;
}

export function computeScore(i: ScoreInputs): number {
  return i.enemiesDefeated * 10 + i.wavesCleared * 100 + i.livesRemaining * 50 + (i.won ? 500 : 0);
}

/**
 * Every enemy a level is scripted to send: each wave group's count, the
 * roamer (Gazing Dome's Chiply), and a second form for every enemy that
 * `transformsInto` one when killed. Minion summons are left out — how many
 * there are depends on how long the summoner lives. `/skip`'s perfect score
 * counts these as killed (spec 2026-10-06).
 */
export function scriptedKills(
  level: { waves: readonly WaveDef[]; roamer?: unknown },
  // `true` = a transforming boss (2 kills); a NUMBER is the kills one spawn is
  // worth outright (a Halolight is 7: itself, two halves, four quarters).
  transforms: (enemyType: EnemyType) => boolean | number,
): number {
  let n = level.roamer ? 1 : 0;
  for (const wave of level.waves) {
    for (const g of wave.groups) {
      const k = transforms(g.enemyType);
      n += g.count * (typeof k === 'number' ? k : k ? 2 : 1);
    }
  }
  return n;
}

/**
 * Kills one spawn of `type` is worth when everything it becomes is killed too:
 * 1, plus its second form (`transformsInto`), plus every split
 * (`splitsInto`), recursively. The predicate `scriptedKills` wants.
 */
export function killsPerSpawn(
  enemies: Record<
    string,
    {
      transformsInto?: { enemyType: EnemyType };
      splitsInto?: { enemyType: EnemyType; count: number };
    }
  >,
  type: EnemyType,
): number {
  const spec = enemies[type];
  if (!spec) return 1;
  let n = 1;
  if (spec.transformsInto) n += killsPerSpawn(enemies, spec.transformsInto.enemyType);
  if (spec.splitsInto)
    n += spec.splitsInto.count * killsPerSpawn(enemies, spec.splitsInto.enemyType);
  return n;
}

/** The score of a flawless clear: every scripted kill, every wave, every life. */
export function perfectScore(
  level: Parameters<typeof scriptedKills>[0] & { startLives: number },
  transforms: Parameters<typeof scriptedKills>[1],
): number {
  return computeScore({
    enemiesDefeated: scriptedKills(level, transforms),
    wavesCleared: level.waves.length,
    livesRemaining: level.startLives,
    won: true,
  });
}

// ---------------------------------------------------------------------------
// Star rating + progression gate
// ---------------------------------------------------------------------------

/** Fraction-of-starting-lives needed for 2★ (1★ = any winning finish). */
export const STAR_2_LIVES_FRAC = 0.5;
/** Fraction-of-starting-lives needed for 3★. */
export const STAR_3_LIVES_FRAC = 0.8;

/**
 * Stars required on a level to unlock the next one. Single source of truth for
 * the advancement gate — bump this down to make progression more forgiving.
 *
 * 1★ means "won" (see `starsFor`), so this makes clearing a level the only
 * requirement to advance: how well you cleared it is a score, not a gate.
 * Bosses are gated the same way — but for them "win" means the boss died, so
 * a boss finale still can't be passed without actually beating it.
 */
export const REQUIRED_STARS_TO_ADVANCE = 1;

/**
 * Star rating (0–3) for a finished level, based on how many lives survived.
 *   3★ — finished with ≥80% of starting lives
 *   2★ — finished with ≥50% of starting lives
 *   1★ — won with at least one life remaining
 *   0★ — lost (or no lives / invalid input)
 */
export function starsFor(livesRemaining: number, startLives: number, won: boolean): 0 | 1 | 2 | 3 {
  if (!won || livesRemaining <= 0 || startLives <= 0) return 0;
  const frac = livesRemaining / startLives;
  if (frac >= STAR_3_LIVES_FRAC) return 3;
  if (frac >= STAR_2_LIVES_FRAC) return 2;
  return 1;
}
